import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AppraisalTemplateDto,
  AppraisalTemplateCreateDto,
  AppraisalTemplateUpdateDto,
  AppraisalTemplateListItemDto
} from '../models';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { AppraisalTemplatePaginatedFilterDto } from '../models/appraisal-template-paginated-filter.dto';
import { PagedResponse } from 'src/app/kuba/shared/models/pagedResponse';
import { param } from 'jquery';
import { AppraisalTemplateBatchStatusDto } from '../models/appraisal-template-batch-status.dto';

@Injectable()
export class AppraisalTemplateService {
  private readonly API_BASE = `${environment.BASE_URL_V2}/appraisal/templates`;

  constructor(private http: HttpClient) {}

  /**
   * Get paginated list of appraisal templates
   * @param filter Pagination and filter parameters
   * @returns Observable of paginated result
   */
  getPagedList(
    businessId: number,
    filter: AppraisalTemplatePaginatedFilterDto
  ): Observable<PagedResponse<AppraisalTemplateListItemDto>> {
    const queryParams = this.mapToHttpParams(filter);

    return this.http
      .get<
        PagedResponse<AppraisalTemplateListItemDto>
      >(`${this.API_BASE}/business/${businessId}/paged`, { params: queryParams })
      .pipe(
        map((result: any) => result),
        catchError(this.handleError)
      );
  }

  /**
   * Get a specific appraisal template by ID
   * @param id Template ID
   * @returns Observable of template details
   */
  getById(id: number): Observable<AppraisalTemplateDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    return this.http.get<AppraisalTemplateDto>(`${this.API_BASE}/${id}`).pipe(
      map((result: any) => result?.data || result),
      catchError(this.handleError)
    );
  }

  /**
   * Create a new appraisal template
   * @param template Template data
   * @returns Observable of created template
   */
  create(
    template: AppraisalTemplateCreateDto
  ): Observable<AppraisalTemplateDto> {
    if (!template) {
      return throwError(() => new Error('Form data is required'));
    }

    if (!template.Name || template.Name.trim() === '') {
      return throwError(() => new Error('Form name is required'));
    }

    if (!template.BusinessId || template.BusinessId <= 0) {
      return throwError(() => new Error('Business ID is required'));
    }

    if (!template.Sections || template.Sections.length === 0) {
      return throwError(() => new Error('At least one section is required'));
    }

    return this.http.post<AppraisalTemplateDto>(this.API_BASE, template).pipe(
      map((result: any) => result?.data || result),
      catchError(this.handleError)
    );
  }

  /**
   * Update an existing appraisal template
   * @param id Template ID
   * @param template Updated template data
   * @returns Observable of updated template
   */
  update(
    id: number,
    template: AppraisalTemplateUpdateDto
  ): Observable<AppraisalTemplateDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    if (!template) {
      return throwError(() => new Error('Form data is required'));
    }

    if (!template.Name || template.Name.trim() === '') {
      return throwError(() => new Error('Form name is required'));
    }

    if (!template.Sections || template.Sections.length === 0) {
      return throwError(() => new Error('At least one section is required'));
    }

    return this.http
      .put<AppraisalTemplateDto>(`${this.API_BASE}/${id}`, template)
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Delete an appraisal template
   * @param id Template ID
   * @returns Observable of deletion result
   */
  delete(id: number): Observable<any> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    return this.http.delete(`${this.API_BASE}/${id}`).pipe(
      map((result: any) => result),
      catchError(this.handleError)
    );
  }

  /**
   * Delete multiple appraisal templates
   * @param ids Array of template IDs
   * @returns Observable of deletion result
   */
  deleteMultiple(ids: number[]): Observable<any> {
    if (!ids || ids.length === 0) {
      return throwError(
        () => new Error('At least one form ID is required')
      );
    }

    return this.http.delete(`${this.API_BASE}/batch`, { body: ids }).pipe(
      map((result: any) => result),
      catchError(this.handleError)
    );
  }

  /**
   * Toggle active status of a template
   * @param id Template ID
   * @returns Observable of updated template
   */
  setActiveStatus(id: number): Observable<any> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    return this.http.patch(`${this.API_BASE}/${id}/status`, {}).pipe(
      map((result: any) => result),
      catchError(this.handleError)
    );
  }

  /**
   * Update status of multiple templates
   * @param templateStatus Template batch status data
   * @returns Observable of result message
   */
  batchUpdateStatus(
    templateStatus: AppraisalTemplateBatchStatusDto
  ): Observable<any> {
    return this.http
      .patch(`${this.API_BASE}/batch/status`, templateStatus)
      .pipe(
        map((result: any) => result),
        catchError(this.handleError)
      );
  }

  /**
   * Clone an existing template
   * @param id Template ID to clone
   * @param newName Name for the cloned template
   * @returns Observable of cloned template
   */
  clone(id: number, newName: string): Observable<AppraisalTemplateDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    if (!newName || newName.trim() === '') {
      return throwError(() => new Error('New form name is required'));
    }

    return this.http
      .post<AppraisalTemplateDto>(`${this.API_BASE}/${id}/clone`, {
        name: newName
      })
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Check if template name is unique within a business
   * @param businessId Business ID
   * @param name Template name
   * @param version Template version
   * @param excludeId Optional template ID to exclude from check (for updates)
   * @returns Observable of boolean indicating if name is available
   */
  checkNameAvailability(
    businessId: number,
    name: string,
    version: number,
    excludeId?: number
  ): Observable<boolean> {
    if (!businessId || businessId <= 0) {
      return throwError(() => new Error('Invalid business ID'));
    }

    if (!name || name.trim() === '') {
      return throwError(() => new Error('Form name is required'));
    }

    let params = new HttpParams().set('name', name).set('version', version);

    if (excludeId && excludeId > 0) {
      params = params.set('excludeId', excludeId);
    }

    return this.http
      .get<boolean>(`${this.API_BASE}/business/${businessId}/check-name`, {
        params
      })
      .pipe(
        map((result: any) => result?.IsAvailable ?? result),
        catchError(this.handleError)
      );
  }

  /**
   * Export template to JSON
   * @param id Template ID
   * @returns Observable of template JSON
   */
  exportTemplate(id: number): Observable<any> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid form ID'));
    }

    return this.http.get(`${this.API_BASE}/${id}/export`).pipe(
      map((result: any) => result),
      catchError(this.handleError)
    );
  }

  /**
   * Import template from JSON
   * @param businessId Business ID
   * @param templateData Template JSON data
   * @returns Observable of imported template
   */
  importTemplate(
    businessId: number,
    templateData: any
  ): Observable<AppraisalTemplateDto> {
    if (!businessId || businessId <= 0) {
      return throwError(() => new Error('Invalid business ID'));
    }

    if (!templateData) {
      return throwError(() => new Error('Form data is required'));
    }

    return this.http
      .post<AppraisalTemplateDto>(`${this.API_BASE}/import`, {
        businessId,
        templateData
      })
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  private mapToHttpParams<T extends Record<string, any>>(dto: T): HttpParams {
    let params = new HttpParams();

    Object.entries(dto).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  /**
   * Handle HTTP errors
   * @param error HTTP error response
   * @returns Observable error
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';
    const serverMessage = this.extractServerMessage(error.error);

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      // Backend error
      if (error.status === 0) {
        errorMessage =
          'Unable to connect to the server. Please check your connection.';
      } else if (error.status === 400) {
        errorMessage = serverMessage || 'Invalid request data';
      } else if (error.status === 401) {
        errorMessage = 'Unauthorized. Please log in again.';
      } else if (error.status === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      } else if (error.status === 404) {
        errorMessage = 'The requested resource was not found.';
      } else if (error.status === 409) {
        errorMessage =
          serverMessage ||
          'A conflict occurred. The resource may already exist.';
      } else if (error.status === 422) {
        errorMessage =
          serverMessage || 'Validation failed. Please check your input.';
      } else if (error.status >= 500) {
        errorMessage = 'A server error occurred. Please try again later.';
      } else {
        errorMessage = serverMessage || `Server Error: ${error.status}`;
      }
    }

    console.error('AppraisalTemplateService Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }

  private extractServerMessage(errorBody: any): string | null {
    if (!errorBody) {
      return null;
    }

    if (typeof errorBody === 'string') {
      return errorBody;
    }

    if (Array.isArray(errorBody)) {
      return errorBody.join(', ');
    }

    if (typeof errorBody?.message === 'string') {
      return errorBody.message;
    }

    return null;
  }
}
